/**
 * Field-report upload helpers.
 * Routes photos and signatures to OneDrive via the backend so we never store
 * base64 image data inside MongoDB. Each helper accepts an already-compressed
 * File/Blob (callers should keep using browser-image-compression).
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Upload a photo file to OneDrive.
 * @param {string} formType - 'precons' | 'soft-digs' | 'locates'
 * @param {string} formId   - parent form id (use 'unsaved' if not yet saved)
 * @param {File|Blob} file  - compressed image file
 * @param {string} token    - auth bearer
 * @returns {Promise<{url: string, public_id: string, storage: string}>}
 */
export async function uploadFieldReportPhoto(formType, formId, file, token) {
  // Make the filename unique per upload — mobile cameras often hand us
  // `image.jpg` for every single photo, and OneDrive PUT-to-path REPLACES
  // any existing file with the same name in the same folder. That caused
  // "batch uploaded 10 photos but only 1 shows" — every PUT was rewriting
  // the same OneDrive item. We append a timestamp + random suffix to
  // guarantee a unique filename for every upload.
  const orig = file.name || 'photo.jpg';
  const dot = orig.lastIndexOf('.');
  const base = dot > 0 ? orig.slice(0, dot) : orig;
  const ext = dot > 0 ? orig.slice(dot) : '.jpg';
  const stamp = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const uniqueName = `${base}_${stamp}${ext}`;

  const fd = new FormData();
  fd.append('form_type', formType);
  fd.append('form_id', formId || 'unsaved');
  fd.append('file', file, uniqueName);
  const res = await axios.post(`${API}/api/field-reports/upload-photo`, fd, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
}

/**
 * Upload a base64 signature (data URL or raw base64) to OneDrive.
 * @returns {Promise<{url: string, public_id: string, storage: string}>}
 */
export async function uploadFieldReportSignature(formType, formId, signatureDataUrl, token) {
  const fd = new FormData();
  fd.append('form_type', formType);
  fd.append('form_id', formId || 'unsaved');
  fd.append('signature_data', signatureDataUrl);
  const res = await axios.post(`${API}/api/field-reports/upload-signature`, fd, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
}
